/**
 * iter-66 — Export buttons must remain visible AT ALL TIMES, even
 * with zero results OR when a history date range is selected. They
 * stay ENABLED so the operator can always trigger a download of the
 * current filter — the server returns whatever matches (possibly an
 * empty file). A tiny warning label appears next to the buttons when
 * the on-screen match count is zero so the operator knows why the
 * download will be small.
 *
 * Shared bar that triggers a browser download by hitting a signed URL.
 * Callers pass:
 *   • `endpoint`    — one of `/exports/student-attendance | staff-attendance | sessions`
 *   • `params`      — the same filter query the on-screen list uses
 *   • `matchCount`  — records the caller is currently rendering; shown as
 *                     a hint next to the buttons.
 *
 * The bar does NOT change any state on the page. It hits the API, and
 * the server returns the file with a proper `Content-Disposition`
 * filename encoding workplace + type + date range.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple, FileCsv, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ExportBar({ endpoint, params = {}, matchCount, testIdPrefix = "export", emptyMessage = "No matching records to export" }) {
  const [busy, setBusy] = useState(null);   // "csv" | "xlsx" | null
  // iter-66 — Export buttons must remain visible AT ALL TIMES, even
  // with zero results. They only DISABLE + surface a clear "no matching
  // records" hint so the operator knows the filter just returned an
  // empty slice — not that the feature is unavailable.
  const isEmpty = typeof matchCount === "number" && matchCount === 0;

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(endpoint, {
        params: { ...params, format },
        responseType: "blob",
      });
      // Content-Disposition -> filename (server-generated).
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m?.[1] || `export.${format}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const count = res.headers?.["x-record-count"];
      toast.success(`Downloaded ${filename}${count ? ` (${count} rows)` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-bar`}>
      {typeof matchCount === "number" && (
        <span
          className={`text-xs mr-1 ${isEmpty ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}`}
          data-testid={`${testIdPrefix}-count`}
        >
          {isEmpty ? emptyMessage : `${matchCount} matching record${matchCount === 1 ? "" : "s"}`}
        </span>
      )}
      <Button
        variant="outline" size="sm"
        onClick={() => download("xlsx")}
        disabled={busy !== null}
        data-testid={`${testIdPrefix}-xlsx`}
        title={isEmpty ? emptyMessage : ""}
      >
        <FileXls size={13} weight="duotone" className="mr-1.5" />
        {busy === "xlsx" ? "Exporting…" : "Download Excel"}
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={() => download("csv")}
        disabled={busy !== null}
        data-testid={`${testIdPrefix}-csv`}
        title={isEmpty ? emptyMessage : ""}
      >
        <FileCsv size={13} weight="duotone" className="mr-1.5" />
        {busy === "csv" ? "Exporting…" : "Download CSV"}
      </Button>
    </div>
  );
}
